class CartAvailability extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector('[data-availability-dialog]');
    this.requests = this.querySelector('.cart-availability__requests');
    this.preview = this.querySelector('[data-availability-preview]');
    this.status = this.querySelector('[data-availability-status]');
    this.qrPanel = this.querySelector('[data-availability-qr-panel]');
    this.qrImage = this.querySelector('[data-availability-qr-image]');
    this.checkoutStep = this.querySelector('[data-availability-checkout-step]');
    this.cart = null;
    this.message = '';
    this.reference = '';
  }

  connectedCallback() {
    this.querySelector('[data-availability-open]')?.addEventListener('click', () => this.open());
    this.querySelector('[data-availability-close]')?.addEventListener('click', () => this.dialog.close());
    this.querySelector('[data-availability-send]')?.addEventListener('click', () => this.sendToWhatsApp());
    this.querySelector('[data-availability-copy]')?.addEventListener('click', () => this.copyDetails());
    this.querySelector('[data-availability-qr]')?.addEventListener('click', () => this.showQr());
    this.querySelector('[data-availability-checkout]')?.addEventListener('click', (event) => this.continueToCheckout(event));
    document.querySelectorAll('button[name="checkout"], .cart__dynamic-checkout-buttons').forEach((element) => {
      element.hidden = true;
    });
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
  }

  async open() {
    this.setStatus('Preparing your latest cart…');
    this.dialog.showModal();
    this.qrPanel.hidden = true;
    this.checkoutStep.hidden = true;

    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart could not be loaded.');
      this.cart = await response.json();
      if (!this.cart.items?.length) throw new Error('Your cart is empty.');
      this.reference = this.createReference();
      this.message = this.buildMessage(this.cart, this.requests.value.trim());
      this.preview.textContent = this.message;
      this.setStatus('');
    } catch (error) {
      this.preview.textContent = '';
      this.setStatus(error.message || 'Something went wrong. Please refresh and try again.', true);
    }
  }

  createReference() {
    const now = new Date();
    const pad = (value) => String(value).padStart(2, '0');
    return `MS-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  }

  money(cents, currency) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
    } catch {
      return `${currency} ${(cents / 100).toFixed(2)}`;
    }
  }

  buildMessage(cart, requests) {
    const currency = cart.currency || 'BZD';
    const lines = [
      `Hello ${this.dataset.shopName},`,
      '',
      'Please confirm availability for this order:',
      `Reference: ${this.reference}`,
      '',
    ];

    cart.items.forEach((item, index) => {
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      lines.push(`${index + 1}. ${item.product_title}`);
      if (variant) lines.push(`   Option: ${variant}`);
      if (item.sku) lines.push(`   SKU: ${item.sku}`);
      Object.entries(item.properties || {}).forEach(([name, value]) => {
        if (name.charAt(0) !== '_' && value) lines.push(`   ${name}: ${value}`);
      });
      lines.push(`   Quantity: ${item.quantity}`);
      lines.push(`   Unit price: ${this.money(item.final_price, currency)}`);
      lines.push(`   Line total: ${this.money(item.final_line_price, currency)}`);
      if (item.url) lines.push(`   Product: ${new URL(item.url, window.location.origin).href}`);
      lines.push('');
    });

    lines.push(`Cart subtotal: ${this.money(cart.total_price, currency)}`);
    lines.push(`Currency: ${currency}`);
    if (requests) {
      lines.push('', 'Order requests:', requests);
    }
    lines.push('', 'Please tell me which items are available and the payment options.');
    return lines.join('\n');
  }

  whatsappUrl(message) {
    const number = (this.dataset.whatsappNumber || '').replace(/\D/g, '');
    return `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
  }

  async saveRequest() {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        note: this.requests.value.trim(),
        attributes: {
          'Availability request': 'Pending',
          'Availability reference': this.reference,
          'Availability requested at': new Date().toISOString(),
        },
      }),
    });
    if (!response.ok) throw new Error('Your request could not be saved to the cart.');
  }

  async sendToWhatsApp() {
    if (!this.message) return;
    const whatsappWindow = window.open('', '_blank');
    if (whatsappWindow) whatsappWindow.opener = null;
    this.setStatus('Saving your request…');
    try {
      await this.saveRequest();
      const url = this.whatsappUrl(this.message);
      if (whatsappWindow) {
        whatsappWindow.location.replace(url);
      } else {
        window.location.href = url;
      }
      this.checkoutStep.hidden = false;
      this.setStatus('Request saved. WhatsApp was opened in a new window. If it did not open, copy the details or use the QR code.');
    } catch (error) {
      whatsappWindow?.close();
      this.setStatus(error.message || 'Something went wrong. Please try again.', true);
    }
  }

  async copyDetails() {
    if (!this.message) return;
    try {
      await navigator.clipboard.writeText(this.message);
      this.setStatus('Order details copied. You can paste them into WhatsApp.');
      this.checkoutStep.hidden = false;
    } catch {
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(this.preview);
      selection.removeAllRanges();
      selection.addRange(range);
      this.setStatus('The order details are selected. Choose Copy, then paste them into WhatsApp.');
      this.checkoutStep.hidden = false;
    }
  }

  async continueToCheckout(event) {
    event.preventDefault();
    this.setStatus('Saving your request before checkout…');
    try {
      await this.saveRequest();
      window.location.assign(this.dataset.checkoutUrl || '/checkout');
    } catch (error) {
      this.setStatus(error.message || 'Your request could not be saved. Please try again.', true);
    }
  }

  compactQrMessage() {
    const maxLength = 760;
    if (this.message.length <= maxLength) return this.message;
    return `${this.message.slice(0, maxLength - 95)}\n\n[Compact QR summary — copy the full order details for the complete request.]`;
  }

  showQr() {
    if (!this.message || typeof window.qrcode !== 'function') {
      this.setStatus('The QR generator is still loading. Please try again in a moment.', true);
      return;
    }
    try {
      window.qrcode.stringToBytes = (value) => Array.from(new TextEncoder().encode(value));
      const qr = window.qrcode(0, 'L');
      qr.addData(this.whatsappUrl(this.compactQrMessage()), 'Byte');
      qr.make();
      this.qrImage.innerHTML = qr.createSvgTag({ cellSize: 4, margin: 16, scalable: true, alt: 'WhatsApp order QR code' });
      this.qrPanel.hidden = false;
      this.checkoutStep.hidden = false;
      this.setStatus('QR code ready to scan.');
    } catch {
      this.setStatus('This order is too large for a QR code. Please use Copy order details instead.', true);
    }
  }

  setStatus(message, isError = false) {
    this.status.textContent = message;
    this.status.classList.toggle('cart-availability__status--error', isError);
  }
}

if (!customElements.get('cart-availability')) {
  customElements.define('cart-availability', CartAvailability);
}
